import { useEffect, useId, useState, type FormEvent } from 'react';
import type { AssetKind, CustomerType, TaskPriority, TaskStatus } from '@shared/types';
import {
  createQuickAddDraft,
  type QuickAddDomain,
  type QuickAddDraftsByDomain
} from '@renderer/app/state/quick-add-store';
import { useShellStore } from '@renderer/app/state/shell-store';
import { createContract } from '@renderer/domains/contracts/contracts-repository';
import { createCustomer } from '@renderer/domains/customers/customers-repository';
import { createInvoice } from '@renderer/domains/invoices/invoices-repository';
import { createLead } from '@renderer/domains/leads/leads-repository';
import { createProject } from '@renderer/domains/projects/projects-repository';
import { createAsset } from '@renderer/domains/resources/resources-repository';
import { createTask } from '@renderer/domains/tasks/tasks-repository';
import { Icon, normalizeBusinessIconName } from '@renderer/shared/components/icons';
import {
  PanelCard,
  SearchableSelectField,
  SelectField,
  TextAreaField,
  TextField,
  type SearchableSelectOption
} from '@renderer/shared/components/primitives';
import { useI18n } from '@renderer/shared/i18n';

type Translate = (key: string, values?: Record<string, string | number>) => string;

export interface QuickAddOption {
  value: string;
  label: string;
}

interface NamedRecord {
  id: string;
  name: string;
}

interface ProjectOptionRecord extends NamedRecord {
  customerId?: string | null;
}

interface CategoryOptionRecord extends NamedRecord {
  iconName?: string | null;
  archivedAt?: string | null;
}

export interface QuickAddContext {
  customerOptions: QuickAddOption[];
  projectOptions: QuickAddOption[];
  customerCategoryOptions: SearchableSelectOption[];
}

export interface QuickAddSubmitResult {
  domain: QuickAddDomain;
  entityId: string;
  route: string;
}

export function getQuickAddDomainLabel(domain: QuickAddDomain, t: Translate): string {
  switch (domain) {
    case 'projects':
      return t('navigation.projects');
    case 'leads':
      return t('navigation.leads');
    case 'tasks':
      return t('navigation.tasks');
    case 'customers':
      return t('navigation.customers');
    case 'resources':
      return t('navigation.resources');
    case 'contracts':
      return t('navigation.contracts');
    case 'invoices':
      return t('navigation.invoices');
  }
}

export function createQuickAddContext({
  customers = [],
  projects = [],
  customerCategories = [],
  t
}: {
  customers?: NamedRecord[];
  projects?: ProjectOptionRecord[];
  customerCategories?: CategoryOptionRecord[];
  t: Translate;
}): QuickAddContext {
  const customerNameById = new Map(customers.map((customer) => [customer.id, customer.name]));
  const customerOptions = [
    { value: '', label: t('common.notSet') },
    ...[...customers].sort((a, b) => a.name.localeCompare(b.name)).map((customer) => ({
      value: customer.id,
      label: customer.name
    }))
  ];
  const projectOptions = [
    { value: '', label: t('common.notSet') },
    ...[...projects].sort((a, b) => a.name.localeCompare(b.name)).map((project) => {
      const customerName = project.customerId ? customerNameById.get(project.customerId) : null;

      return {
        value: project.id,
        label: customerName ? `${project.name} - ${customerName}` : project.name
      };
    })
  ];
  const customerCategoryOptions = [...customerCategories].sort((a, b) => a.name.localeCompare(b.name)).map((category) => {
    const option: SearchableSelectOption = {
      value: category.id,
      label: category.name
    };

    if (category.iconName) {
      option.icon = normalizeBusinessIconName(category.iconName);
    }

    if (category.archivedAt) {
      option.description = t('common.archived');
    }

    return option;
  });

  return {
    customerOptions,
    projectOptions,
    customerCategoryOptions
  };
}

function toNullableText(value: string): string | null {
  const nextValue = value.trim();
  return nextValue ? nextValue : null;
}

function toTags(value: string): string[] {
  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function toIsoDate(value: string): string {
  return `${value}T12:00:00.000Z`;
}

export async function submitQuickAddDomain(
  domain: QuickAddDomain,
  draft: QuickAddDraftsByDomain[QuickAddDomain],
  t: Translate
): Promise<QuickAddSubmitResult> {
  switch (domain) {
    case 'projects': {
      const projectDraft = draft as QuickAddDraftsByDomain['projects'];
      if (!projectDraft.name.trim()) {
        throw new Error(t('quickAdd.validation.nameRequired'));
      }
      if (!projectDraft.customerId) {
        throw new Error(t('quickAdd.validation.customerRequired'));
      }

      const detail = await createProject({
        name: projectDraft.name.trim(),
        customerId: projectDraft.customerId
      });

      return { domain, entityId: detail.project.id, route: `/projects/${detail.project.id}` };
    }
    case 'leads': {
      const leadDraft = draft as QuickAddDraftsByDomain['leads'];
      if (!leadDraft.name.trim()) {
        throw new Error(t('leads.quickAdd.validation.nameRequired'));
      }

      const detail = await createLead({
        name: leadDraft.name.trim(),
        companyName: toNullableText(leadDraft.companyName),
        contactName: toNullableText(leadDraft.contactName),
        phone: toNullableText(leadDraft.phone),
        email: toNullableText(leadDraft.email),
        currentWebsiteUrl: toNullableText(leadDraft.currentWebsiteUrl),
        city: toNullableText(leadDraft.city),
        source: toNullableText(leadDraft.source),
        customerCategoryId: leadDraft.customerCategoryId || null,
        status: 'to_call',
        tags: toTags(leadDraft.tags)
      });

      return { domain, entityId: detail.lead.id, route: `/leads/${detail.lead.id}` };
    }
    case 'tasks': {
      const taskDraft = draft as QuickAddDraftsByDomain['tasks'];
      if (!taskDraft.title.trim()) {
        throw new Error(t('quickAdd.validation.titleRequired'));
      }

      const detail = await createTask({
        title: taskDraft.title.trim(),
        description: toNullableText(taskDraft.description),
        status: taskDraft.status,
        projectId: taskDraft.projectId || null,
        dueAt: taskDraft.dueAt ? toIsoDate(taskDraft.dueAt) : null,
        priority: taskDraft.priority ? (taskDraft.priority as TaskPriority) : null
      });

      return { domain, entityId: detail.task.id, route: '/tasks' };
    }
    case 'customers': {
      const customerDraft = draft as QuickAddDraftsByDomain['customers'];
      if (!customerDraft.name.trim()) {
        throw new Error(t('quickAdd.validation.nameRequired'));
      }

      const detail = await createCustomer({
        name: customerDraft.name.trim(),
        type: customerDraft.type
      });

      return { domain, entityId: detail.customer.id, route: `/customers/${detail.customer.id}` };
    }
    case 'resources': {
      const resourceDraft = draft as QuickAddDraftsByDomain['resources'];
      if (!resourceDraft.name.trim()) {
        throw new Error(t('quickAdd.validation.nameRequired'));
      }

      const detail = await createAsset({
        name: resourceDraft.name.trim(),
        kind: resourceDraft.kind,
        mimeType: toNullableText(resourceDraft.mimeType),
        storagePath: toNullableText(resourceDraft.storagePath),
        projectId: resourceDraft.projectId || null,
        customerId: resourceDraft.customerId || null
      });

      return { domain, entityId: detail.asset.id, route: `/resources/${detail.asset.id}` };
    }
    case 'contracts': {
      const contractDraft = draft as QuickAddDraftsByDomain['contracts'];
      if (!contractDraft.title.trim()) {
        throw new Error(t('quickAdd.validation.titleRequired'));
      }
      if (!contractDraft.customerId) {
        throw new Error(t('quickAdd.validation.customerRequired'));
      }

      const detail = await createContract({
        customerId: contractDraft.customerId,
        title: contractDraft.title.trim(),
        projectId: contractDraft.projectId || null
      });

      return { domain, entityId: detail.contract.id, route: `/contracts/${detail.contract.id}` };
    }
    case 'invoices': {
      const invoiceDraft = draft as QuickAddDraftsByDomain['invoices'];
      if (!invoiceDraft.customerId) {
        throw new Error(t('quickAdd.validation.customerRequired'));
      }

      const detail = await createInvoice({
        customerId: invoiceDraft.customerId,
        projectId: invoiceDraft.projectId || null
      });

      return { domain, entityId: detail.invoice.id, route: `/invoices/${detail.invoice.id}` };
    }
  }
}

export function QuickAddFields({
  context,
  domain,
  draft,
  updateDraft,
  variant = 'inline'
}: {
  context: QuickAddContext;
  domain: QuickAddDomain;
  draft: QuickAddDraftsByDomain[QuickAddDomain];
  updateDraft: <D extends QuickAddDomain>(domain: D, patch: Partial<QuickAddDraftsByDomain[D]>) => void;
  variant?: 'inline' | 'modal';
}): JSX.Element {
  const { t } = useI18n();
  const gridClassName = variant === 'inline' ? 'av-inline-quick-add__grid' : 'av-form-grid';
  const taskStatusOptions: QuickAddOption[] = (['backlog', 'todo', 'in_progress', 'done', 'archived'] satisfies TaskStatus[]).map(
    (value) => ({
      value,
      label: t(`enums.taskStatus.${value}`)
    })
  );
  const taskPriorityOptions: QuickAddOption[] = [
    { value: '', label: t('common.notSet') },
    { value: 'low', label: t('enums.taskPriority.low') },
    { value: 'medium', label: t('enums.taskPriority.medium') },
    { value: 'high', label: t('enums.taskPriority.high') }
  ];
  const assetKindOptions: QuickAddOption[] = (['logo', 'icon', 'document', 'image', 'pdf'] satisfies AssetKind[]).map((value) => ({
    value,
    label: t(`enums.assetKind.${value}`)
  }));
  const customerTypeOptions: QuickAddOption[] = (['company', 'sole_trader', 'individual'] satisfies CustomerType[]).map((value) => ({
    value,
    label: t(`enums.customerType.${value}`)
  }));

  switch (domain) {
    case 'projects': {
      const projectDraft = draft as QuickAddDraftsByDomain['projects'];
      return (
        <div className={gridClassName}>
          <label className="av-form-field">
            <span>{t('quickAdd.fields.name')}</span>
            <TextField name="project-name" value={projectDraft.name} onValueChange={(value) => updateDraft('projects', { name: value })} />
          </label>
          <label className="av-form-field">
            <span>{t('quickAdd.fields.customer')}</span>
            <SelectField
              value={projectDraft.customerId}
              options={context.customerOptions}
              onChange={(event) => updateDraft('projects', { customerId: event.target.value })}
            />
          </label>
        </div>
      );
    }
    case 'leads': {
      const leadDraft = draft as QuickAddDraftsByDomain['leads'];
      return (
        <div className={gridClassName}>
          <label className="av-form-field">
            <span>{t('leads.editor.fields.name')}</span>
            <TextField name="lead-name" value={leadDraft.name} onValueChange={(value) => updateDraft('leads', { name: value })} />
          </label>
          <label className="av-form-field">
            <span>{t('leads.editor.fields.companyName')}</span>
            <TextField
              name="lead-company"
              value={leadDraft.companyName}
              onValueChange={(value) => updateDraft('leads', { companyName: value })}
            />
          </label>
          <label className="av-form-field">
            <span>{t('leads.editor.fields.contactName')}</span>
            <TextField
              name="lead-contact"
              value={leadDraft.contactName}
              onValueChange={(value) => updateDraft('leads', { contactName: value })}
            />
          </label>
          <label className="av-form-field">
            <span>{t('leads.editor.fields.phone')}</span>
            <TextField name="lead-phone" value={leadDraft.phone} onValueChange={(value) => updateDraft('leads', { phone: value })} />
          </label>
          <label className="av-form-field">
            <span>{t('leads.editor.fields.email')}</span>
            <TextField name="lead-email" value={leadDraft.email} onValueChange={(value) => updateDraft('leads', { email: value })} />
          </label>
          <label className="av-form-field">
            <span>{t('leads.editor.fields.currentWebsiteUrl')}</span>
            <TextField
              name="lead-website"
              value={leadDraft.currentWebsiteUrl}
              onValueChange={(value) => updateDraft('leads', { currentWebsiteUrl: value })}
            />
          </label>
          <label className="av-form-field">
            <span>{t('leads.editor.fields.city')}</span>
            <TextField name="lead-city" value={leadDraft.city} onValueChange={(value) => updateDraft('leads', { city: value })} />
          </label>
          <label className="av-form-field">
            <span>{t('leads.editor.fields.source')}</span>
            <TextField name="lead-source" value={leadDraft.source} onValueChange={(value) => updateDraft('leads', { source: value })} />
          </label>
          <label className="av-form-field">
            <span>{t('leads.editor.fields.customerCategory')}</span>
            <SearchableSelectField
              clearLabel={t('common.clear')}
              emptyLabel={t('common.noResults')}
              options={context.customerCategoryOptions}
              placeholder={t('leads.filters.categoryPlaceholder')}
              searchPlaceholder={t('leads.filters.searchCategory')}
              value={leadDraft.customerCategoryId}
              onValueChange={(value) => updateDraft('leads', { customerCategoryId: value })}
            />
          </label>
          <label className="av-form-field">
            <span>{t('leads.editor.fields.tags')}</span>
            <TextField name="lead-tags" value={leadDraft.tags} onValueChange={(value) => updateDraft('leads', { tags: value })} />
          </label>
        </div>
      );
    }
    case 'tasks': {
      const taskDraft = draft as QuickAddDraftsByDomain['tasks'];
      return (
        <div className={gridClassName}>
          <label className="av-form-field">
            <span>{t('quickAdd.fields.title')}</span>
            <TextField name="task-title" value={taskDraft.title} onValueChange={(value) => updateDraft('tasks', { title: value })} />
          </label>
          <label className="av-form-field">
            <span>{t('quickAdd.fields.description')}</span>
            <TextAreaField
              name="task-description"
              value={taskDraft.description}
              onValueChange={(value) => updateDraft('tasks', { description: value })}
            />
          </label>
          <label className="av-form-field">
            <span>{t('tasks.fields.status')}</span>
            <SelectField
              value={taskDraft.status}
              options={taskStatusOptions}
              onChange={(event) => updateDraft('tasks', { status: event.target.value as TaskStatus })}
            />
          </label>
          <label className="av-form-field">
            <span>{t('quickAdd.fields.priority')}</span>
            <SelectField
              value={taskDraft.priority}
              options={taskPriorityOptions}
              onChange={(event) => updateDraft('tasks', { priority: event.target.value as TaskPriority | '' })}
            />
          </label>
          <label className="av-form-field">
            <span>{t('quickAdd.fields.project')}</span>
            <SelectField
              value={taskDraft.projectId}
              options={context.projectOptions}
              onChange={(event) => updateDraft('tasks', { projectId: event.target.value })}
            />
          </label>
          <label className="av-form-field">
            <span>{t('quickAdd.fields.dueAt')}</span>
            <TextField
              name="task-due-at"
              type="date"
              value={taskDraft.dueAt}
              onValueChange={(value) => updateDraft('tasks', { dueAt: value })}
            />
          </label>
        </div>
      );
    }
    case 'customers': {
      const customerDraft = draft as QuickAddDraftsByDomain['customers'];
      return (
        <div className={gridClassName}>
          <label className="av-form-field">
            <span>{t('quickAdd.fields.name')}</span>
            <TextField
              name="customer-name"
              value={customerDraft.name}
              onValueChange={(value) => updateDraft('customers', { name: value })}
            />
          </label>
          <label className="av-form-field">
            <span>{t('quickAdd.fields.legalType')}</span>
            <SelectField
              value={customerDraft.type}
              options={customerTypeOptions}
              onChange={(event) => updateDraft('customers', { type: event.target.value as CustomerType })}
            />
          </label>
        </div>
      );
    }
    case 'resources': {
      const resourceDraft = draft as QuickAddDraftsByDomain['resources'];
      return (
        <div className={gridClassName}>
          <label className="av-form-field">
            <span>{t('quickAdd.fields.name')}</span>
            <TextField
              name="resource-name"
              value={resourceDraft.name}
              onValueChange={(value) => updateDraft('resources', { name: value })}
            />
          </label>
          <label className="av-form-field">
            <span>{t('quickAdd.fields.category')}</span>
            <SelectField
              value={resourceDraft.kind}
              options={assetKindOptions}
              onChange={(event) => updateDraft('resources', { kind: event.target.value as AssetKind })}
            />
          </label>
          <label className="av-form-field">
            <span>{t('quickAdd.fields.project')}</span>
            <SelectField
              value={resourceDraft.projectId}
              options={context.projectOptions}
              onChange={(event) => updateDraft('resources', { projectId: event.target.value })}
            />
          </label>
          <label className="av-form-field">
            <span>{t('quickAdd.fields.customer')}</span>
            <SelectField
              value={resourceDraft.customerId}
              options={context.customerOptions}
              onChange={(event) => updateDraft('resources', { customerId: event.target.value })}
            />
          </label>
          <label className="av-form-field">
            <span>{t('quickAdd.fields.mimeType')}</span>
            <TextField
              name="resource-mime-type"
              value={resourceDraft.mimeType}
              onValueChange={(value) => updateDraft('resources', { mimeType: value })}
            />
          </label>
          <label className="av-form-field">
            <span>{t('quickAdd.fields.storagePath')}</span>
            <TextField
              name="resource-storage-path"
              value={resourceDraft.storagePath}
              onValueChange={(value) => updateDraft('resources', { storagePath: value })}
            />
          </label>
        </div>
      );
    }
    case 'contracts': {
      const contractDraft = draft as QuickAddDraftsByDomain['contracts'];
      return (
        <div className={gridClassName}>
          <label className="av-form-field">
            <span>{t('quickAdd.fields.title')}</span>
            <TextField
              name="contract-title"
              value={contractDraft.title}
              onValueChange={(value) => updateDraft('contracts', { title: value })}
            />
          </label>
          <label className="av-form-field">
            <span>{t('quickAdd.fields.customer')}</span>
            <SelectField
              value={contractDraft.customerId}
              options={context.customerOptions}
              onChange={(event) => updateDraft('contracts', { customerId: event.target.value })}
            />
          </label>
          <label className="av-form-field">
            <span>{t('quickAdd.fields.project')}</span>
            <SelectField
              value={contractDraft.projectId}
              options={context.projectOptions}
              onChange={(event) => updateDraft('contracts', { projectId: event.target.value })}
            />
          </label>
        </div>
      );
    }
    case 'invoices': {
      const invoiceDraft = draft as QuickAddDraftsByDomain['invoices'];
      return (
        <div className={gridClassName}>
          <label className="av-form-field">
            <span>{t('quickAdd.fields.customer')}</span>
            <SelectField
              value={invoiceDraft.customerId}
              options={context.customerOptions}
              onChange={(event) => updateDraft('invoices', { customerId: event.target.value })}
            />
          </label>
          <label className="av-form-field">
            <span>{t('quickAdd.fields.project')}</span>
            <SelectField
              value={invoiceDraft.projectId}
              options={context.projectOptions}
              onChange={(event) => updateDraft('invoices', { projectId: event.target.value })}
            />
          </label>
        </div>
      );
    }
  }
}

export function InlineQuickAddPanel({
  context,
  domain,
  onCreated
}: {
  context: QuickAddContext;
  domain: QuickAddDomain;
  onCreated?: (result: QuickAddSubmitResult) => void | Promise<void>;
}): JSX.Element {
  const { t } = useI18n();
  const panelId = useId();
  const preferences = useShellStore((state) => state.preferences);
  const savePreferences = useShellStore((state) => state.savePreferences);
  const [draft, setDraft] = useState<QuickAddDraftsByDomain[QuickAddDomain]>(() => createQuickAddDraft(domain));
  const [status, setStatus] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const expandedPreferences = preferences.inlineQuickAddExpanded ?? {};
  const expanded = expandedPreferences[domain] ?? false;
  const isBusy = status === 'submitting';
  const domainLabel = getQuickAddDomainLabel(domain, t);

  useEffect(() => {
    setDraft(createQuickAddDraft(domain));
    setStatus('idle');
    setError(null);
  }, [domain]);

  function updateDraft<D extends QuickAddDomain>(targetDomain: D, patch: Partial<QuickAddDraftsByDomain[D]>): void {
    if (targetDomain !== domain) {
      return;
    }

    setDraft((current) => ({
      ...current,
      ...patch
    }));
  }

  function toggleExpanded(): void {
    void savePreferences({
      inlineQuickAddExpanded: {
        ...expandedPreferences,
        [domain]: !expanded
      }
    });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();

    if (isBusy) {
      return;
    }

    setError(null);
    setStatus('submitting');

    try {
      const result = await submitQuickAddDomain(domain, draft, t);
      await onCreated?.(result);
      setDraft(createQuickAddDraft(domain));
      setStatus('idle');
    } catch (nextError) {
      setStatus('error');
      setError(nextError instanceof Error ? nextError.message : 'Create failed.');
    }
  }

  return (
    <PanelCard
      className="av-inline-quick-add-panel"
      title={t('quickAdd.inlineTitle', { domain: domainLabel })}
      actions={
        <button
          aria-controls={panelId}
          aria-expanded={expanded}
          aria-label={expanded ? t('quickAdd.actions.collapseInline') : t('quickAdd.actions.expandInline')}
          className={['av-inline-quick-add__toggle', expanded ? 'is-expanded' : ''].filter(Boolean).join(' ')}
          type="button"
          onClick={toggleExpanded}
        >
          <Icon name="chevron" />
        </button>
      }
    >
      {expanded ? (
        <form id={panelId} className="av-inline-quick-add" onSubmit={(event) => void handleSubmit(event)}>
          <QuickAddFields context={context} domain={domain} draft={draft} updateDraft={updateDraft} />
          {error ? <div className="av-inline-error">{error}</div> : null}
          <div className="av-inline-quick-add__actions">
            <button className="av-primary-button" disabled={isBusy} type="submit">
              <Icon name="plus" />
              <span>{domain === 'leads' ? t('leads.quickAdd.actions.add') : t('shell.create')}</span>
            </button>
          </div>
        </form>
      ) : null}
    </PanelCard>
  );
}
